import React, { useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import Shell from "@/components/Shell";
import PatternsStrip from "@/components/PatternsStrip";
import WeatherStrip from "@/components/WeatherStrip";
import AgentActivityStrip from "@/components/AgentActivityStrip";
import RightNowPanel from "@/components/RightNowPanel";
import { api } from "@/lib/api";
import { Loader2, Sparkles, Copy, Check, Download } from "lucide-react";

export default function Brief() {
    const [brief, setBrief] = useState("");
    const [count, setCount] = useState(0);
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState(""); // live status while streaming
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState(false);
    const [toast, setToast] = useState(""); // brief instant-feedback message

    const flash = (msg, ms = 3500) => {
        setToast(msg);
        setTimeout(() => setToast((t) => (t === msg ? "" : t)), ms);
    };

    // Iter 31.43 — background-job + polling. Replaces the SSE streamer
    // because production's ingress enforces a hard 30s max-response-
    // duration cap that bytes-flowing-through doesn't reset (proved by
    // No. 6's 25 Feb 2026 frame trail: 15 perfect heartbeats then a cut
    // at exactly 29,992ms). The new pattern splits the work across many
    // short HTTP exchanges — start (<100ms) + repeated status polls
    // (<100ms each) — so no single request ever approaches the cap.
    const pollTimer = useRef(null);
    const cancelledRef = useRef(false);

    // Stop any in-flight poll when the component unmounts.
    useEffect(() => () => {
        cancelledRef.current = true;
        if (pollTimer.current) clearTimeout(pollTimer.current);
    }, []);

    const generate = async () => {
        flash("Print Brief triggered — No. 2 is at the typewriter…");
        setBusy(true);
        setErr("");
        setStatus("Opening file…");
        setBrief("");
        cancelledRef.current = false;

        const t0 = Date.now();
        const polls = []; // [{at_ms, status}] — diagnostic trail for incident-log

        try {
            // 1. Kick off the background job. Returns in <100ms.
            const { data: startResp } = await api.post("/shed/brief/start", {});
            const jobId = startResp.job_id;
            // Iter 31.49 — Brief costs 1 credit, charged at start.
            // Refresh the Shell pill so the operator sees the debit.
            window.dispatchEvent(new Event("shed:credits-changed"));
            setStatus("No. 2 is at the typewriter…");

            // 2. Poll for completion. 1.5s cadence (iter 31.44 — was 2s);
            //    ceiling 180s (well past Claude's worst-case 90s).
            //    Each poll is a tiny GET that completes inside any proxy cap.
            const POLL_INTERVAL_MS = 1500;
            const MAX_WAIT_MS = 180_000;
            const deadline = Date.now() + MAX_WAIT_MS;

            // Iter 31.44 — stage-specific status copy. Backend writes
            // `stage` into the job doc as it progresses; here we map
            // it to operator-facing text so the UI doesn't show a
            // single stale "distilling…" between polls.
            const stageCopy = {
                pending: "Opening the file…",
                reading_ledger: "Reading the ledger…",
                awaiting_handler: "No. 2 is at the typewriter…",
                filing: "Filing the brief on the desk…",
                done: "",
                timeout: "Handler took too long — try again.",
                crashed: "Generator crashed — see /incidents.",
            };

            while (true) {  // eslint-disable-line no-constant-condition
                if (cancelledRef.current) return;
                if (Date.now() > deadline) {
                    throw new Error(
                        `Brief polling timed out after ${Math.round(MAX_WAIT_MS / 1000)}s. `
                        + `Job ${jobId} may still be running server-side — `
                        + `try again in a minute.`,
                    );
                }
                await new Promise((resolve) => {
                    pollTimer.current = setTimeout(resolve, POLL_INTERVAL_MS);
                });
                if (cancelledRef.current) return;

                const { data: job } = await api.get(`/shed/brief/status/${jobId}`);
                polls.push({ at_ms: Date.now() - t0, status: job.status, stage: job.stage });

                if (job.status === "running" || job.status === "pending") {
                    const elapsedS = Math.round((Date.now() - t0) / 1000);
                    const stageText = stageCopy[job.stage] || "No. 2 is at the typewriter…";
                    setStatus(`${stageText} ${elapsedS}s`);
                    continue;
                }
                if (job.status === "error") {
                    throw new Error(job.error || "Brief generation failed");
                }
                if (job.status === "empty") {
                    // Iter 31.93 — empty ledger. Credit refunded server-side.
                    setStatus("");
                    setErr(job.error || "No ledger entries yet. Credit refunded.");
                    return;
                }
                if (job.status === "done") {
                    setBrief(job.brief || "");
                    setCount(job.entries_considered || 0);
                    setStatus("");
                    const dispatched = Array.isArray(job.auto_dispatched_to)
                        ? job.auto_dispatched_to
                        : [];
                    const dispatchedLine = dispatched.length > 0
                        ? ` · auto-dispatched to ${dispatched.join(" · ")}`
                        : "";
                    flash(
                        `Brief filed · ${job.entries_considered || 0} entries on the desk${dispatchedLine}`,
                        5000,
                    );
                    return;
                }
                // unknown status — keep polling, but record the anomaly
            }
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message || "Brief generation failed";
            setErr(`[brief] ${msg}`);
            setStatus("");
            // File the failure to /incidents with the poll trail so a
            // post-mortem can see WHEN the polling died (HTTP error vs
            // timeout vs job-status=error).
            try {
                await api.post("/admin/incident-log", {
                    kind: "brief_job_failed",
                    message: msg,
                    path: "/brief",
                    context: {
                        fired_at: new Date().toISOString(),
                        polls,
                        total_elapsed_ms: Date.now() - t0,
                        http_status: e?.response?.status || null,
                    },
                    user_agent: navigator.userAgent || "",
                });
            } catch (_) { /* incident log itself failed — nothing more we can do */ }
        } finally {
            setBusy(false);
            if (pollTimer.current) {
                clearTimeout(pollTimer.current);
                pollTimer.current = null;
            }
        }
    };

    const copyError = async () => {
        try { await navigator.clipboard.writeText(err); } catch { /* no-op */ }
    };

    const copy = async () => {
        await navigator.clipboard.writeText(brief);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const download = () => {
        const blob = new Blob([brief], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `me-brief-${new Date().toISOString().slice(0, 10)}.md`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const renderMarkdown = (md) => {
        const lines = md.split("\n");
        const out = [];
        let list = [];
        // Inline markdown → HTML. Iter 28.3: every fragment passes through
        // DOMPurify before render to prevent stored-XSS via a crafted Brief
        // (e.g. if a future LinkedIn import or Mureka drop ever surfaced
        // raw HTML in the Brief body). Default profile strips <script>,
        // event handlers, javascript: URLs etc. while keeping <strong>/<em>.
        const inline = (s) =>
            DOMPurify.sanitize(
                String(s || "")
                    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-stamp-red">$1</strong>')
                    .replace(/\*(.+?)\*/g, '<em class="text-ink-soft">$1</em>'),
                { USE_PROFILES: { html: true } }
            );
        const flushList = () => {
            if (list.length) {
                out.push(
                <ul key={`ul-${out.length}`} className="list-disc ml-6 mb-4 space-y-1">
                    {list.map((li, i) => (
                        <li
                            key={`li-${out.length}-${i}-${li.slice(0, 20)}`}
                            className="font-body text-sm text-ink"
                            dangerouslySetInnerHTML={{ __html: inline(li) }}
                        />
                    ))}
                </ul>
                );
                list = [];
            }
        };
        lines.forEach((raw, i) => {
            const line = raw.trim();
            if (line.startsWith("# ")) {
                flushList();
                out.push(
                    <h1 key={i} className="font-heading text-4xl uppercase tracking-[0.05em] text-stamp-red mt-8 mb-4 first:mt-0">
                        {line.slice(2)}
                    </h1>
                );
            } else if (line.startsWith("## ")) {
                flushList();
                out.push(
                    <h2 key={i} className="font-heading text-2xl uppercase tracking-[0.05em] text-ink mt-6 mb-3">
                        {line.slice(3)}
                    </h2>
                );
            } else if (line.startsWith("### ")) {
                flushList();
                out.push(
                    <h3 key={i} className="label-typed text-stamp-red mt-4 mb-2">
                        {line.slice(4)}
                    </h3>
                );
            } else if (line.startsWith("---")) {
                flushList();
                out.push(<div key={i} className="hairline my-6" />);
            } else if (line.startsWith("- ")) {
                list.push(line.slice(2));
            } else if (line === "") {
                flushList();
            } else {
                flushList();
                out.push(
                    <p key={i} className="font-body text-sm text-ink mb-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(line) }} />
                );
            }
        });
        flushList();
        return out;
    };

    return (
        <Shell>
            {toast && (
                <div
                    className="fixed top-20 left-1/2 -translate-x-1/2 z-50 paper-card border-2 border-stamp-red px-5 py-2 shadow-lg animate-in fade-in slide-in-from-top duration-200"
                    data-testid="brief-toast"
                >
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-stamp-red" />
                        <span className="font-heading uppercase tracking-[0.18em] text-[11px] text-ink">
                            {toast}
                        </span>
                    </div>
                </div>
            )}
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">For the next handler · Paste at the start</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1">
                        The Brief<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        A printed dossier for the next No. 2 — Claude, DeepSeek, whoever picks up
                        the phone. Slide it under the door before the conversation and they'll know
                        who they're dealing with.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {brief && (
                        <>
                            <button
                                onClick={copy}
                                className="inline-flex items-center gap-2 border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-4 py-2 font-heading uppercase text-xs tracking-[0.25em]"
                                data-testid="brief-copy-button"
                            >
                                {copied ? <Check className="w-3 h-3 text-stamp-green" /> : <Copy className="w-3 h-3" />}
                                {copied ? "Copied" : "Copy"}
                            </button>
                            <button
                                onClick={download}
                                className="inline-flex items-center gap-2 border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-4 py-2 font-heading uppercase text-xs tracking-[0.25em]"
                                data-testid="brief-download-button"
                            >
                                <Download className="w-3 h-3" /> Download
                            </button>
                        </>
                    )}
                    <button
                        onClick={generate}
                        disabled={busy}
                        className="inline-flex items-center gap-2 bg-ink text-paper-light hover:bg-stamp-red px-5 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-50"
                        data-testid="brief-generate-button"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        {brief ? "Reprint" : "Print Brief"}
                    </button>
                </div>
            </div>

            {/* Agent activity · last 24h — self-reporting headline. (iter 31.29) */}
            <AgentActivityStrip />

            {/* Devices ↔ Patterns strip — observed habits, deadpan headline. */}
            <PatternsStrip />

            {/* Right Now — single GREEN/AMBER/RED verdict synthesising Lunar +
                Circadian + Weather. Sits between the strips so it reads as a
                summary of the data above and below. (iter 30.6, 19 Feb 2026) */}
            <div className="my-4">
                <RightNowPanel />
            </div>

            {/* Weather Power — lagged 48h productivity score from sunshine hours. */}
            <WeatherStrip />

            {err && (
                <div className="mb-6 paper-card border-2 border-stamp-red p-4" data-testid="brief-error">
                    <div className="flex items-center justify-between mb-2">
                        <div className="stamp-red font-heading text-sm tracking-[0.2em]">
                            FAILURE ON FILE
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={copyError}
                                className="border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper-light px-3 py-1 font-heading uppercase text-[10px] tracking-[0.2em]"
                                data-testid="brief-error-copy"
                            >
                                Copy
                            </button>
                            <button
                                onClick={() => setErr("")}
                                className="border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-3 py-1 font-heading uppercase text-[10px] tracking-[0.2em]"
                                data-testid="brief-error-clear"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                    <pre className="font-body text-xs text-ink whitespace-pre-wrap break-words select-text">{err}</pre>
                    <div className="label-typed mt-2 text-[9px]">Stays until cleared.</div>
                </div>
            )}

            {!brief && !busy && (
                <div className="paper-card border border-dashed border-ink/30 p-16 text-center tilt-l" data-testid="brief-empty">
                    <Sparkles className="w-10 h-10 text-ink mx-auto mb-4" strokeWidth={1} />
                    <div className="font-heading text-3xl uppercase tracking-[0.06em] text-ink-faint">
                        No brief on the desk
                    </div>
                    <div className="label-typed mt-3">
                        Hit Print Brief. The office will run the typewriter.
                    </div>
                </div>
            )}

            {busy && (
                <div className="paper-card p-16 text-center tilt-r" data-testid="brief-busy">
                    <Loader2 className="w-8 h-8 text-stamp-red mx-auto mb-4 animate-spin" />
                    <div className="label-typed text-stamp-red">
                        No. 2 is at the typewriter · distilling the file
                    </div>
                    {status && (
                        <div className="mt-3 font-body text-sm text-ink" data-testid="brief-status">
                            {status}
                        </div>
                    )}
                    <div className="mt-2 label-typed text-[9px] italic">
                        Stays connected. Won't time out on mobile.
                    </div>
                </div>
            )}

            {brief && !busy && (
                <>
                    <div className="label-typed mb-2">
                        {count} entries considered · {new Date().toLocaleString()}
                    </div>
                    <article
                        className="paper-card tape-corner p-10 md:p-14 relative"
                        data-testid="brief-content"
                    >
                        <div className="absolute top-4 right-6 classified-stamp">For No. 2's Eyes</div>
                        {renderMarkdown(brief)}
                        <div className="mt-8 pt-4 border-t border-ink/15 flex items-center justify-between">
                            <span className="label-typed">End of file · BJLIAM / 06</span>
                            <span className="pencil text-lg">Be seeing you —</span>
                        </div>
                    </article>
                </>
            )}
        </Shell>
    );
}
